import React, { useState } from "react";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { TekkrMarkdown } from "../../../components/shared/markdown/tekkr-markdown";
import { MessageContainer } from "../../../components/shared/chat/message";
import { cn } from "../../../lib/utils";
import { CheckIcon, Loader2Icon, ArrowDown } from "lucide-react";

export function GoogleDriveCompareStatusBlock(props: { title: string; description: string }) {
    return (
        <MessageContainer role="assistant">
            <div className="flex w-full items-start gap-3 rounded-xl border bg-card p-5 shadow-sm">
                <Loader2Icon className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                <div>
                    <div className="text-base font-semibold">{props.title}</div>
                    <div className="mt-1 text-sm text-muted-foreground">{props.description}</div>
                </div>
            </div>
        </MessageContainer>
    );
}

export interface GoogleDriveCompareBlockProps {
    optionA: string;
    optionB: string;
    onSelect: (selectedOption: "A" | "B", reason?: string) => void;
    isLoading?: boolean;
}

export function GoogleDriveCompareBlock({ optionA, optionB, onSelect, isLoading }: GoogleDriveCompareBlockProps) {
    const [selectedOption, setSelectedOption] = useState<"A" | "B" | null>(null);
    const [reason, setReason] = useState("");

    const handleOptionClick = (option: "A" | "B") => {
        setSelectedOption(option);
    };

    const handleConfirm = () => {
        if (selectedOption) {
            onSelect(selectedOption, reason.trim() || undefined);
        }
    };

    return (
        <MessageContainer role="assistant">
            <div className="flex h-[74vh] min-h-[42rem] w-full flex-col overflow-hidden rounded-2xl border bg-card shadow-sm">
                <div className="shrink-0 border-b px-6 py-5">
                    <p className="text-lg font-semibold">Which response do you prefer?</p>
                    <p className="mt-1 text-sm text-muted-foreground">Read both and choose the one you prefer.</p>
                </div>

                <div className="grid min-h-0 flex-1 gap-5 px-6 py-6 lg:grid-cols-2">
                    <OptionPane
                        label="Response 1"
                        content={optionA}
                        isSelected={selectedOption === "A"}
                        disabled={isLoading}
                        onClick={() => handleOptionClick("A")}
                    />
                    <OptionPane
                        label="Response 2"
                        content={optionB}
                        isSelected={selectedOption === "B"}
                        disabled={isLoading}
                        onClick={() => handleOptionClick("B")}
                    />
                </div>

                {selectedOption && (
                    <div className="shrink-0 space-y-3 border-t bg-card px-6 py-6">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-muted-foreground">
                                What made you prefer this one? (optional)
                            </label>
                            <Textarea
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Tell us why you preferred this option..."
                                className="min-h-[80px] resize-none"
                                maxLength={2000}
                            />
                        </div>

                        <div className="flex justify-center">
                            <Button onClick={handleConfirm} disabled={isLoading} className="h-12 w-full max-w-md">
                                {isLoading ? (
                                    <>
                                        <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                                        Continue...
                                    </>
                                ) : (
                                    <>
                                        <ArrowDown className="mr-2 h-4 w-4" />
                                        Continue
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </MessageContainer>
    );
}

interface OptionCardProps {
    label: string;
    content: string;
    isSelected: boolean;
    disabled?: boolean;
    onClick: () => void;
}

function OptionPane({ label, content, isSelected, disabled, onClick }: OptionCardProps) {
    return (
        <div
            className={cn(
                "flex min-h-0 flex-col overflow-hidden rounded-xl border bg-background transition-all",
                isSelected
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "border-border bg-background hover:bg-accent/50",
                disabled && !isSelected && "opacity-50"
            )}
        >
            <div className="shrink-0 border-b px-5 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-lg font-semibold">{label}</div>
                    </div>
                    {isSelected && (
                        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                            <CheckIcon className="h-3 w-3" />
                        </div>
                    )}
                </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
                <TekkrMarkdown markdown={content} className="prose prose-sm max-w-none text-[15px] leading-7" />
            </div>

            <div className="shrink-0 border-t px-5 py-4">
                <Button type="button" variant="default" className="w-full" disabled={disabled} onClick={onClick}>
                    {isSelected ? "Selected" : "Choose this one"}
                </Button>
            </div>
        </div>
    );
}
